import React, { useState, useEffect } from 'react';
import { Clock, Users, Star, Gift, Utensils, Car, Wifi, Dumbbell } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import BookingModal from '../components/BookingModal';
import PackageDetailsModal from '../components/PackageDetailsModal';
import ImageCarousel from '../components/ImageCarousel';
import CountdownTimer from '../components/CountdownTimer';
import apiClient from '../lib/apiClient';

const Packages = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [packageDetailsOpen, setPackageDetailsOpen] = useState(false);
  const [selectedPackageForDetails, setSelectedPackageForDetails] = useState(null);
  const [packagesData, setPackagesData] = useState({
    hero: {
      title: "Special Packages & Offers",
      subtitle: "Discover our curated packages designed for unforgettable experiences",
      backgroundImage: "https://images.unsplash.com/photo-1611048267451-e6ed903d4a38?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHxob3RlbCUyMGludGVyaW9yfGVufDB8fHx8MTc1ODI2NzU5N3ww&ixlib=rb-4.1.0&q=85"
    },
    categories: [
      { id: 'all', name: 'All Packages' },
      { id: 'romantic', name: 'Romantic' },
      { id: 'business', name: 'Business' },
      { id: 'family', name: 'Family' },
      { id: 'wellness', name: 'Wellness' }
    ],
    packages: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch packages from backend
  useEffect(() => {
    const fetchPackages = async () => {
      try {
        setLoading(true);
        const backendPackages = await apiClient.get('/api/Packages/GetAll');
        if (Array.isArray(backendPackages)) {
          setPackagesData(prev => ({
            ...prev,
            packages: backendPackages
          }));
        } else {
          console.warn('Failed to fetch packages from backend, using static data');
          // Keep static data as fallback
          setPackagesData(prev => ({
            ...prev,
            packages: getStaticPackages()
          }));
        }
      } catch (error) {
        console.error('Error fetching packages:', error);
        // Use static data as fallback
        setPackagesData(prev => ({
          ...prev,
          packages: getStaticPackages()
        }));
      } finally {
        setLoading(false);
      }
    };

    // For demo purposes, using static data with advanced features
    // In production, this would fetch from backend
    setPackagesData(prev => ({
      ...prev,
      packages: getStaticPackages()
    }));
    setLoading(false);
    
    // fetchPackages(); // Uncomment when backend integration is needed
  }, []);

  // Static packages as fallback
  const getStaticPackages = () => [
      {
        id: 1,
        name: "Weekend Romantic Getaway",
        category: "romantic",
        images: [
          "https://images.unsplash.com/photo-1618773928121-c32242e63f39?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwzfHxob3RlbCUyMHJvb218ZW58MHx8fHwxNzU4MzU1MjgzfDA&ixlib=rb-4.1.0&q=85",
          "https://images.unsplash.com/photo-1591088398332-8a7791972843?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHw0fHxob3RlbCUyMHJvb218ZW58MHx8fHwxNzU4MzU1MjgzfDA&ixlib=rb-4.1.0&q=85",
          "https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHw1fHxob3RlbCUyMHJvb218ZW58MHx8fHwxNzU4MzU1MjgzfDA&ixlib=rb-4.1.0&q=85"
        ],
        price: 59900,
        originalPrice: 74900,
        discount: 20, // 20% discount
        duration: "2 nights",
        maxGuests: 2,
        rating: 4.9,
        reviews: 156,
        description: "Perfect romantic escape with luxury accommodation, candlelit dinner, and spa treatments for couples.",
        highlights: [
          "Luxury suite with fireplace",
          "Couples spa treatment",
          "Candlelit dinner for two",
          "Champagne & chocolates",
          "Late checkout (2 PM)"
        ],
        inclusions: [
          { icon: Gift, text: "Welcome amenities" },
          { icon: Utensils, text: "Romantic dinner" },
          { icon: Star, text: "Spa credit $100" },
          { icon: Car, text: "Valet parking" }
        ],
        popular: true,
        hasCountdown: false
      },
      {
        id: 2,
        name: "Business Executive Package",
        category: "business",
        images: [
          "https://images.unsplash.com/photo-1629140727571-9b5c6f6267b4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxob3RlbCUyMGludGVyaW9yfGVufDB8fHx8MTc1ODI2NzU5N3ww&ixlib=rb-4.1.0&q=85",
          "https://images.unsplash.com/photo-1596394516093-501ba68a0ba6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxob3RlbCUyMGludGVyaW9yfGVufDB8fHx8MTc1ODI2NzU5N3ww&ixlib=rb-4.1.0&q=85"
        ],
        price: 44900,
        originalPrice: 52900,
        discount: 15, // 15% discount
        duration: "3 nights",
        maxGuests: 1,
        rating: 4.8,
        reviews: 243,
        description: "Comprehensive business package with meeting facilities, executive lounge access, and productivity amenities.",
        highlights: [
          "Executive room upgrade",
          "Meeting room (4 hours daily)",
          "Business center access",
          "Airport transfers",
          "Express laundry service"
        ],
        inclusions: [
          { icon: Wifi, text: "Premium Wi-Fi" },
          { icon: Utensils, text: "Business breakfast" },
          { icon: Car, text: "Airport transfer" },
          { icon: Gift, text: "Executive lounge access" }
        ],
        popular: false,
        hasCountdown: false
      },
      {
        id: 3,
        name: "Family Fun Adventure",
        category: "family",
        images: [
          "https://images.unsplash.com/photo-1611892440504-42a792e24d32?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxob3RlbCUyMHJvb218ZW58MHx8fHwxNzU4MzU1MjgzfDA&ixlib=rb-4.1.0&q=85",
          "https://images.unsplash.com/photo-1571896349842-33c89424de2d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxob3RlbCUyMHJvb218ZW58MHx8fHwxNzU4MzU1MjgzfDA&ixlib=rb-4.1.0&q=85",
          "https://images.unsplash.com/photo-1564501049412-61c2a3083791?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwzfHxob3RlbCUyMHJvb218ZW58MHx8fHwxNzU4MzU1MjgzfDA&ixlib=rb-4.1.0&q=85"
        ],
        price: 79900,
        originalPrice: 94900,
        discount: 16, // 16% discount
        duration: "4 nights",
        maxGuests: 4,
        rating: 4.7,
        reviews: 189,
        description: "Perfect family package with connecting rooms, kids activities, and family-friendly amenities.",
        highlights: [
          "Family suite or connecting rooms",
          "Kids club activities",
          "Family pool access",
          "Children's meal plan",
          "Babysitting services available"
        ],
        inclusions: [
          { icon: Users, text: "Family accommodation" },
          { icon: Utensils, text: "Kids eat free" },
          { icon: Gift, text: "Activity vouchers" },
          { icon: Car, text: "Family parking" }
        ],
        popular: true,
        hasCountdown: true,
        countdownEndTime: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000 + 5 * 60 * 60 * 1000).toISOString() // 2 days 5 hours from now
      },
      {
        id: 4,
        name: "Honeymoon Bliss Special",
        category: "romantic",
        images: [
          "https://images.unsplash.com/photo-1578683010236-d716f9a3f461?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBob3RlbHxlbnwwfHx8fDE3NTgzNTUyNzN8MA&ixlib=rb-4.1.0&q=85",
          "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBob3RlbHxlbnwwfHx8fDE3NTgzNTUyNzN8MA&ixlib=rb-4.1.0&q=85",
          "https://images.unsplash.com/photo-1540553016722-983e48a2cd10?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBob3RlbHxlbnwwfHx8fDE3NTgzNTUyNzN8MA&ixlib=rb-4.1.0&q=85"
        ],
        price: 99900,
        originalPrice: 119900,
        discount: 17, // 17% discount  
        duration: "5 nights",
        maxGuests: 2,
        rating: 5.0,
        reviews: 98,
        description: "Ultimate honeymoon experience with luxury suite, private dining, and exclusive romantic amenities.",
        highlights: [
          "Honeymoon suite with ocean view",
          "Private dining experience",
          "Couples spa day",
          "Rose petal turndown service",
          "Photographer session (1 hour)"
        ],
        inclusions: [
          { icon: Gift, text: "Champagne & roses" },
          { icon: Utensils, text: "Private dinner" },
          { icon: Star, text: "Couples spa package" },
          { icon: Car, text: "Luxury transfers" }
        ],
        popular: false,
        hasCountdown: true,
        countdownEndTime: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000 + 12 * 60 * 60 * 1000).toISOString() // 1 day 12 hours from now
      },
      {
        id: 5,
        name: "Wellness Retreat Package",
        category: "wellness",
        images: [
          "https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBob3RlbHxlbnwwfHx8fDE3NTgzNTUyNzN8MA&ixlib=rb-4.1.0&q=85",
          "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBob3RlbHxlbnwwfHx8fDE3NTgzNTUyNzN8MA&ixlib=rb-4.1.0&q=85"
        ],
        price: 69900,
        originalPrice: 82900,
        discount: 16, // 16% discount
        duration: "3 nights",
        maxGuests: 2,
        rating: 4.8,
        reviews: 167,
        description: "Rejuvenating wellness package focusing on relaxation, spa treatments, and healthy dining options.",
        highlights: [
          "Spa suite accommodation",
          "Daily spa treatments",
          "Yoga and meditation sessions",
          "Healthy cuisine menu",
          "Fitness center access"
        ],
        inclusions: [
          { icon: Dumbbell, text: "Fitness access" },
          { icon: Star, text: "Daily spa treatment" },
          { icon: Utensils, text: "Wellness menu" },
          { icon: Gift, text: "Wellness amenities" }
        ],
        popular: true,
        hasCountdown: false
      },
      {
        id: 6,
        name: "Corporate Team Building",
        category: "business",
        images: [
          "https://images.unsplash.com/photo-1621293954908-907159247fc8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxob3RlbCUyMGludGVyaW9yfGVufDB8fHx8MTc1ODI2NzU5N3ww&ixlib=rb-4.1.0&q=85",
          "https://images.unsplash.com/photo-1597218868981-1b68e15f0065?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHxob3RlbCUyMGludGVyaW9yfGVufDB8fHx8MTc1ODI2NzU5N3ww&ixlib=rb-4.1.0&q=85"
        ],
        price: 129900,
        originalPrice: 149900,
        discount: 13, // 13% discount
        duration: "2 nights",
        maxGuests: 8,
        rating: 4.6,
        reviews: 78,
        description: "Comprehensive corporate package for team building events with meeting facilities and group activities.",
        highlights: [
          "Group accommodation",
          "Conference room (full day)",
          "Team building activities",
          "Group dining options",
          "Audio/visual equipment"
        ],
        inclusions: [
          { icon: Users, text: "Group rates" },
          { icon: Wifi, text: "Conference facilities" },
          { icon: Utensils, text: "Group meals" },
          { icon: Gift, text: "Team activities" }
        ],
        popular: false,
        hasCountdown: true,
        countdownEndTime: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000 + 8 * 60 * 60 * 1000).toISOString() // 3 days 8 hours from now
      }
    ];  // End of getStaticPackages function
  
  const filteredPackages = selectedCategory === 'all' 
    ? packagesData.packages 
    : packagesData.packages.filter(pkg => pkg.category === selectedCategory);

  const handleBookPackage = (pkg) => {
    setSelectedPackage(pkg);
    setBookingModalOpen(true);
  };

  const handleViewDetails = (pkg) => {
    setSelectedPackageForDetails(pkg);
    setPackageDetailsOpen(true);
  };

  if (loading) {
    return (
      <div className="overflow-hidden">
        <section className="relative h-96 flex items-center justify-center bg-gray-100">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading packages...</p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section 
        className="relative h-96 flex items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(30, 64, 175, 0.7), rgba(30, 64, 175, 0.7)), url(${packagesData.hero.backgroundImage})`
        }}
      >
        <div className="text-center text-white z-10 max-w-4xl px-6">
          <h1 className="mb-4 text-white">{packagesData.hero.title}</h1>
          <p className="text-xl">{packagesData.hero.subtitle}</p>
        </div>
      </section>

      {/* Packages Section */}
      <section className="section-large">
        <div className="container">
          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {packagesData.categories.map((category) => (
              <button
                key={category.id}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                  selectedCategory === category.id
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                onClick={() => setSelectedCategory(category.id)}
              >
                {category.name}
              </button>
            ))}
          </div>

          {/* Packages Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPackages.map((pkg) => (
              <Card key={pkg.id} className="overflow-hidden group flex flex-col h-full">
                {/* Package Image Carousel */}
                <div className="relative aspect-video overflow-hidden flex-shrink-0">
                  <ImageCarousel
                    images={pkg.images}
                    className="w-full h-full"
                    autoPlay={true}
                    interval={4000}
                    showDots={false}
                  />
                  
                  {/* Badges */}
                  <div className="absolute top-4 left-4 flex flex-col gap-2">
                    {pkg.popular && (
                      <div className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                        Popular
                      </div>
                    )}
                    {pkg.discount && (
                      <div className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                        {pkg.discount}% OFF
                      </div>
                    )}
                  </div>
                  
                  {/* Price Display */}
                  <div className="absolute top-4 right-4 bg-blue-600/90 backdrop-blur-sm text-white px-3 py-2 rounded-lg">
                    <div className="text-lg font-bold">৳{pkg.price.toLocaleString()}</div>
                    <div className="text-xs opacity-90">{pkg.duration}</div>
                  </div>
                </div>

                {/* Package Content */}
                <div className="p-6 flex flex-col flex-grow">
                  <h3 className="text-xl font-semibold mb-2">{pkg.name}</h3>
                  
                  {/* Countdown Timer for Limited Time Offers */}
                  {pkg.hasCountdown && (
                    <div className="mb-3">
                      <CountdownTimer
                        endTime={pkg.countdownEndTime}
                        className="text-sm"
                        onExpire={() => console.log(`Package ${pkg.id} offer expired`)}
                      />
                    </div>
                  )}
                  
                  {/* Rating and Reviews */}
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="flex items-center">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          size={16}
                          className={`${
                            i < Math.floor(pkg.rating)
                              ? 'text-yellow-400 fill-current'
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-sm text-gray-600">
                      {pkg.rating} ({pkg.reviews} reviews)
                    </span>
                  </div>

                  <p className="text-gray-600 mb-4 leading-relaxed">
                    {pkg.description}
                  </p>

                  {/* Package Details */}
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    <div className="flex items-center">
                      <Clock size={16} className="mr-1" />
                      {pkg.duration}
                    </div>
                    <div className="flex items-center">
                      <Users size={16} className="mr-1" />
                      Up to {pkg.maxGuests} guests
                    </div>
                    <div className="flex items-center">
                      <span className="line-through mr-2">৳{pkg.originalPrice.toLocaleString()}</span>
                      <span className="text-green-600 font-semibold">
                        Save ৳{(pkg.originalPrice - pkg.price).toLocaleString()}
                      </span>
                    </div>
                  </div>

                  {/* Inclusions */}
                  <div className="mb-6">
                    <h4 className="font-semibold mb-3">Package Includes:</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {pkg.inclusions.map((inclusion, index) => (
                        <div key={index} className="flex items-center space-x-2 text-sm text-gray-700">
                          <inclusion.icon size={14} className="text-blue-600 flex-shrink-0" />
                          <span>{inclusion.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-3 mt-auto">
                    <Button
                      onClick={() => handleBookPackage(pkg)}
                      className="w-full"
                    >
                      Book This Package
                    </Button>
                    <Button 
                      onClick={() => handleViewDetails(pkg)}
                      variant="outline" 
                      className="w-full"
                    >
                      View Full Details
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* No Packages Message */}
          {filteredPackages.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">No packages found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* Special Offers Section */}
      <section className="section-large bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="container text-center">
          <h2 className="mb-6 text-white">Special Seasonal Offers</h2>
          <p className="text-xl mb-8 text-blue-100 max-w-3xl mx-auto">
            Don't miss our limited-time seasonal packages and exclusive offers. 
            Book now and save up to 25% on selected packages.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-8 py-4">
              View All Offers
            </Button>
            <Button variant="outline" className="text-lg px-8 py-4">
              Subscribe for Updates
            </Button>
          </div>
        </div>
      </section>

      {/* Package Details Modal */}
      <PackageDetailsModal
        isOpen={packageDetailsOpen}
        onClose={() => setPackageDetailsOpen(false)}
        package={selectedPackageForDetails}
        onBookPackage={handleBookPackage}
      />

      {/* Booking Modal */}
      <BookingModal
        isOpen={bookingModalOpen}
        onClose={() => setBookingModalOpen(false)}
        roomData={null}
        packageData={selectedPackage}
      />
    </div>
  );
};

export default Packages;